import { useContext } from 'react';
import { useAuthenticated, useDataProvider } from 'react-admin';

import { AppContext } from '@app/contexts';
import { useGenericList } from '@app/hooks/useGenericList';
import { CASLAction, useAbility } from '@app/utils/authUtils';
import { getFieldOnMainLanguage } from '@app/utils/languages';
import { prependHttpsIfProtocolNotPresent } from '@app/utils/urlUtils';

import { GenericList } from '@app/components/common/GenericList';
import { ColumnTypes, DeleteConfirmation, GenericObject } from '@app/components/common/Table/DefaultTable/types';

export const ListCompanies = () => {
  const { handleChangeLoading } = useContext(AppContext);
  const ability = useAbility();
  const dataProvider = useDataProvider();

  const {
    isLoading,
    data,
    total,
    handleChangeSort,
    handleChangeFilters,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useGenericList('companies', { sort: { field: 'date_created', order: 'DESC' } });

  /**
   * List[X] pages require manual auth check for some reason.
   * This involves the `checkAuth` method in auth.ts.
   */
  useAuthenticated();

  const resolveDeleteConfirmationContent = async (companyId: number): Promise<DeleteConfirmation> => {
    const response = await dataProvider.fetchLinks('companies', companyId);
    const links = response?.data;
    return {
      deletable: true,
      message:
        links.Subscribers > 0
          ? `This company is currently being followed by ${links.Subscribers} on the website. Are you sure you want to delete this company?`
          : 'Are you sure you want to delete this company?',
    } as DeleteConfirmation;
  };

  const removeHTMLTag = (input: string) => {
    const updated = input.replace(/<[^>]*>/g, '');
    return updated;
  };

  const columns = [
    {
      label: 'NAME',
      key: 'short_name',
      getImagePath: (record: GenericObject) => `${(record?.logo_file_base as GenericObject)?.path}`,
      type: ColumnTypes.TITLE,
      format: (row: GenericObject) => `${getFieldOnMainLanguage(row?.short_name)}`,
      sort: (order: string) => {
        handleChangeSort({ field: 'short_name', order });
      },
    },
    {
      label: 'FULL COMPANY NAME',
      key: 'name',
      format: (row: GenericObject) => getFieldOnMainLanguage(row?.name),
      type: ColumnTypes.DEFAULT,
      sort: (order: string) => {
        handleChangeSort({ field: 'company.name', order });
      },
    },
    {
      label: 'INDUSTRY',
      key: 'industry',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => `${getFieldOnMainLanguage((row?.industry as GenericObject)?.name)}`,
      sort: (order: string) => {
        handleChangeSort({ field: 'Industry.name', order });
      },
    },
    {
      label: 'TICKER',
      key: 'chix_symbol',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => (row?.chix_symbol as GenericObject)?.code as string,
      sort: (order: string) => {
        handleChangeSort({ field: 'ChixSymbol.code', order });
      },
    },
    {
      label: 'DESCRIPTION',
      key: 'description',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => {
        const description = getFieldOnMainLanguage(row?.description);
        const strippedDescription = removeHTMLTag(description);
        return strippedDescription;
      },
    },
    {
      label: 'TWITTER',
      key: 'twitter_handle',
      type: ColumnTypes.LINK_NEW_BROWSER_TAB,
      format: (row: GenericObject) => `${row?.twitter_handle ?? ''}`,
      getLink: (row: GenericObject) =>
        row?.twitter_handle ? prependHttpsIfProtocolNotPresent(row?.twitter_handle as string) : '',
    },
    {
      label: 'Sponsored',
      key: 'is_new_ui',
      type: ColumnTypes.STATUS,
      // @ts-ignore this is definitely valid
      publishedKey: 'is_new_ui',
      isPublished: (row: GenericObject) => !!row.is_new_ui,
      getNewPublishedStatus: (row: GenericObject) => !row.is_new_ui,
    },
  ];

  if (ability.can(CASLAction.WRITE, 'companies')) {
    columns.push({
      label: 'PUBLISHED',
      key: 'company_profile',
      type: ColumnTypes.STATUS,
      // @ts-ignore this is definitely valid
      publishedKey: 'company_profile',
      isPublished: (row: GenericObject) => !!row.company_profile,
      getNewPublishedStatus: (row: GenericObject) => !row.company_profile,
    });
  }

  return (
    <GenericList
      isListLoading={isLoading}
      rows={data}
      resourceSingular="company"
      generalFilterKey={'name'}
      handleChangeLoading={handleChangeLoading}
      columns={columns}
      resource="companies"
      title="Companies"
      page={page}
      rowsPerPage={rowsPerPage}
      handleChangePage={handleChangePage}
      total={total}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      handleChangeFilters={handleChangeFilters}
      resolveDeleteConfirmationContent={resolveDeleteConfirmationContent}
    />
  );
};
